import React from 'react';
import { styled } from '@mui/material/styles';
import {useTranslation} from 'react-i18next';
import PropTypes from 'prop-types';
import {
  Typography,
  Drawer,
  Tooltip,
} from '@mui/material';
import TreeView from '@mui/lab/TreeView';
import TreeItem from '@mui/lab/TreeItem';
import {alpha} from '@mui/material/styles';
import { useHistory } from "react-router-dom";

const PREFIX = 'DRAWER_WIDTH';

const classes = {
  drawer: `${PREFIX}-drawer`,
  drawerPaper: `${PREFIX}-drawerPaper`,
  drawerHeader: `${PREFIX}-drawerHeader`,
  drawerSection: `${PREFIX}-drawerSection`,
  drawerSectionIndex: `${PREFIX}-drawerSectionIndex`,
  closeBtn: `${PREFIX}-closeBtn`,
  listIcon: `${PREFIX}-listIcon`,
  root: `${PREFIX}-root`,
  content: `${PREFIX}-content`,
  group: `${PREFIX}-group`,
  expanded: `${PREFIX}-expanded`,
  selected: `${PREFIX}-selected`,
  label: `${PREFIX}-label`,
  labelRoot: `${PREFIX}-labelRoot`,
  labelIcon: `${PREFIX}-labelIcon`,
  labelText: `${PREFIX}-labelText`
};

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Root = styled('div')((
  {
    theme
  }
) => ({
  [`& .${classes.drawer}`]: {
    boxSizing: 'border-box',
    width: DRAWER_WIDTH,
    flexShrink: 0,
    zIndex: '10 !important',
  },

  [`& .${classes.drawerPaper}`]: {
    boxSizing: 'border-box',
    width: DRAWER_WIDTH,
    top: 'auto',
    paddingBottom: theme.spacing(2),
    paddingRight: theme.spacing(1.25),
    zIndex: '10 !important',
    overflowX: 'hidden',
  },

  [`& .${classes.drawerHeader}`]: {
    'display': 'flex',
    'alignItems': 'center',
    'zIndex': 10,
    'position': 'sticky',
    'top': 0,
    'background': 'white',
    '& > svg': {
      marginRight: theme.spacing(2),
    },
    'padding': theme.spacing(3, 0, 2, 3),
  },

  [`& .${classes.drawerSection}`]: {
    padding: theme.spacing(3, 2, 0, 2),
    flexShrink: 0,
  },

  [`& .${classes.drawerSectionIndex}`]: {
    zIndex: -10,
  },

  [`& .${classes.closeBtn}`]: {
    flexGrow: 1,
    textAlign: 'right',
  },

  [`& .${classes.listIcon}`]: {
    color: theme.palette.common.black,
  },

  [`& .${classes.root}`]: {
    'color': theme.palette.text.secondary,
    '&:hover > $content': {
      backgroundColor: theme.palette.action.hover,
    },
    '&:focus > $content, &$selected > $content': {
      backgroundColor: alpha(theme.palette.primary.main, 0.25),
      color: theme.palette.primary.main,
    },
    '&:focus > $content $label, &:hover > $content $label, &$selected > $content $label': {
      backgroundColor: 'transparent',
    },
  },

  [`& .${classes.content}`]: {
    'color': theme.palette.text.secondary,
    'borderTopRightRadius': theme.spacing(2),
    'borderBottomRightRadius': theme.spacing(2),
    'fontWeight': theme.typography.fontWeightMedium,
    '$expanded > &': {
      fontWeight: theme.typography.fontWeightRegular,
    },
    '&>.MuiTreeItem-iconContainer': {
      width: 0,
      margin: 0,
    },
  },

  [`& .${classes.group}`]: {
    'marginLeft': 0,
    '& $content': {
      paddingLeft: theme.spacing(2),
    },
  },

  [`& .${classes.expanded}`]: {},
  [`& .${classes.selected}`]: {},

  [`& .${classes.label}`]: {
    fontWeight: 'inherit',
    color: 'inherit',
    borderTopRightRadius: theme.spacing(2),
    borderBottomRightRadius: theme.spacing(2),
    paddingLeft: 0,
  },

  [`& .${classes.labelRoot}`]: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0.5, 0, 0.5, 0.5),
    paddingLeft: 0,
  },

  [`& .${classes.labelIcon}`]: {
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(2),
  },

  [`& .${classes.labelText}`]: {
    fontWeight: 'inherit',
    flexGrow: 1,
    maxWidth: '150px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  }
}));

export const DRAWER_WIDTH = 240;

function StyledTreeItem(props) {

  const {
    labelText, 
    labelInfo, 
    color, 
    bgColor, 
    ...other
  } = props;

  return (
    <TreeItem
      label={
        <Tooltip
          title={labelText} 
          placement="top" 
          arrow
        >
          <div className={classes.labelRoot}>
            <Typography color="inherit" className={classes.labelIcon} />
            <Typography variant="body2" className={classes.labelText}>
              {labelText}
            </Typography>
            <Typography variant="caption" color="inherit"></Typography>
          </div>
        </Tooltip>
      }
      style={{
        '--tree-view-color': color,
        '--tree-view-bg-color': bgColor,
      }}
      classes={{
        root: classes.root,
        content: classes.content,
        expanded: classes.expanded,
        selected: classes.selected,
        group: classes.group,
        label: classes.label,
      }}
      {...other}
    />
  );
}
StyledTreeItem.propTypes = {
  bgColor: PropTypes.string,
  color: PropTypes.string,
  labelInfo: PropTypes.string,
  labelText: PropTypes.string.isRequired,
};

export default function DashboardDrawer(props) {

  const {t, i18n} = useTranslation();
  const history = useHistory();

  let adminPage = "";
  if(history.location.pathname.length > 1){
    let pathnames = history.location.pathname.split("/");
    adminPage = pathnames[pathnames.length-1];
  }

  const handlePageChange=(e,value)=> {
    history.push("/" + i18n.language + "/" + value);
  };
  
  return (
    (<Root>
      <Drawer
        className={classes.drawer}
        variant="persistent"
        anchor="left"
        open={props.open}
        classes={{
          paper: classes.drawerPaper,
        }}
      >
        <div className={classes.drawerHeader}>
          <Typography variant='subtitle2' component='h2'>
            Admin Pages
          </Typography>
        </div>
        <TreeView
          id='pages-list'
          className={classes.root}
          selected={adminPage}
          onNodeSelect={handlePageChange}
        >
          <StyledTreeItem
            key={1}
            nodeId="manageprojects"
            labelText="Manage projects"
          />
          <StyledTreeItem
            key={2}
            nodeId="manageusers"
            labelText="Manage users"
          />
          <StyledTreeItem
            key={3}
            nodeId="viewcodesets"
            labelText="View codesets"
          />
          <StyledTreeItem
            key={4}
            nodeId="viewhistories"
            labelText="View histories"
          />
        </TreeView>
      </Drawer>
    </Root>)
  );
}
DashboardDrawer.propTypes={
  open: PropTypes.bool.isRequired,
}